"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { bottomLayerFor } from "@/lib/bottom-layer";
import { onHomeClick } from "./home-click";
import { useCartCount } from "@/greenpak/use-cart-count";

/**
 * The phone discovery bar: Home · Resorts · Tours · My Trip.
 *
 * Shown only where `bottomLayerFor` says this page's bottom edge is free —
 * never alongside a booking action or a form's submit. Plain links inside a
 * <nav>, so these four destinations stay part of the crawlable link graph
 * rather than a JavaScript-only affordance.
 *
 * It sets `--pbd-bottom-bar` on the document so the page can reserve the
 * space and the floating chat buttons can sit above it, instead of every
 * consumer hard-coding the same height.
 */
const ITEMS = [
  { href: "/", label: "Home", match: (p: string) => p === "/" },
  { href: "/stays", label: "Resorts", match: (p: string) => p.startsWith("/stays") },
  { href: "/tours", label: "Tours", match: (p: string) => p.startsWith("/tours") },
  { href: "/cart", label: "My Trip", match: (p: string) => p.startsWith("/cart") },
] as const;

function Icon({ name, active }: { name: string; active: boolean }) {
  const stroke = active ? "var(--cta)" : "currentColor";
  const common = { width: 22, height: 22, viewBox: "0 0 24 24", fill: "none", "aria-hidden": true } as const;
  if (name === "Home")
    return (
      <svg {...common}>
        <path d="M4 20V9.5L12 4l8 5.5V20" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9.5 20v-5h5v5" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    );
  // Resorts: a bed, since the house now means Home.
  if (name === "Resorts")
    return (
      <svg {...common}>
        <path d="M3 19V6M3 15h18v4M21 15v-3a3 3 0 0 0-3-3h-7v6" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="7" cy="11.5" r="1.8" stroke={stroke} strokeWidth="1.6" />
      </svg>
    );
  if (name === "Tours")
    return (
      <svg {...common}>
        <path d="M4 17.5 9 6l5 7 2-3 4 7.5" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" />
        <circle cx="9" cy="6" r="1.4" fill={stroke} />
      </svg>
    );
  return (
    <svg {...common}>
      <path d="M6 8h12l-1 11H7L6 8Z" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M9.5 8V6.5a2.5 2.5 0 0 1 5 0V8" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export function MobileBottomNav() {
  // Read in the browser so the page around it can be cached (app/(site)/layout.tsx).
  const cartCount = useCartCount();
  const pathname = usePathname() || "/";
  const show = bottomLayerFor(pathname) === "discovery";
  /**
   * The on-screen keyboard shrinks the visual viewport but not the layout
   * viewport, which would leave this bar stranded halfway up the screen over
   * the keyboard. Hide it while the keyboard is up.
   *
   * A shrunken viewport alone is not enough to go on: a phone browser's own
   * toolbars slide away as you scroll, which shrinks it by a similar amount
   * and made the bar blink out and back on the way down the homepage. So the
   * shrink only counts as a keyboard when something is actually being typed
   * into.
   */
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const check = () => {
      const shrunk = window.innerHeight - vv.height > 140;
      const el = document.activeElement;
      const typing =
        el instanceof HTMLElement &&
        (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      setKeyboardOpen(shrunk && typing);
    };
    check();
    vv.addEventListener("resize", check);
    document.addEventListener("focusin", check);
    document.addEventListener("focusout", check);
    return () => {
      vv.removeEventListener("resize", check);
      document.removeEventListener("focusin", check);
      document.removeEventListener("focusout", check);
    };
  }, []);

  const visible = show && !keyboardOpen;

  useEffect(() => {
    const root = document.documentElement;
    if (visible) root.setAttribute("data-bottom-bar", "discovery");
    else root.removeAttribute("data-bottom-bar");
    return () => root.removeAttribute("data-bottom-bar");
  }, [visible]);

  if (!visible) return null;

  return (
    <>
      {/* A spacer in the normal flow, so the room for the bar exists in the
          first painted frame. Reserving it by way of the attribute above
          meant the page reflowed a moment after load — the content jumping
          as the bar appeared. */}
      <div className="pbd-bottom-nav-spacer" aria-hidden />
      <nav aria-label="Browse" className="pbd-bottom-nav">
      <ul>
        {ITEMS.map((item) => {
          const active = item.match(pathname);
          return (
            <li key={item.href}>
              <Link href={item.href} onClick={(e) => onHomeClick(e, pathname, item.href)} aria-current={active ? "page" : undefined} data-active={active || undefined}>
                <span className="pbd-bottom-nav__icon">
                  <Icon name={item.label} active={active} />
                  {item.label === "My Trip" && cartCount > 0 ? (
                    <span className="pbd-bottom-nav__badge" aria-hidden>
                      {cartCount > 9 ? "9+" : cartCount}
                    </span>
                  ) : null}
                </span>
                <span className="pbd-bottom-nav__label">{item.label}</span>
                {item.label === "My Trip" && cartCount > 0 ? (
                  <span className="sr-only">{cartCount} item(s) in your trip</span>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
      </nav>
    </>
  );
}
